import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { cleoApi } from "./api";

// Situação do modelo na Meta → o que o lojista lê.
const SITUACOES = {
  APPROVED: ["Aprovado", "bg-green-500/15 text-green-700 dark:text-green-300"],
  PENDING: ["Em análise", "bg-amber-500/15 text-amber-700 dark:text-amber-300"],
  IN_APPEAL: ["Em análise", "bg-amber-500/15 text-amber-700 dark:text-amber-300"],
  NAO_CRIADO: ["Aguardando envio", "bg-muted text-muted-foreground"],
  REJECTED: ["Recusado pela Meta", "bg-red-500/15 text-red-700 dark:text-red-300"],
  PAUSED: ["Pausado pela Meta", "bg-red-500/15 text-red-700 dark:text-red-300"],
  DISABLED: ["Desativado pela Meta", "bg-red-500/15 text-red-700 dark:text-red-300"],
  ERRO: ["Não foi criado", "bg-red-500/15 text-red-700 dark:text-red-300"],
};

function selo(m) {
  if (m.situacao === "APPROVED" && !m.em_uso) return ["Aprovado como marketing (não usado)", SITUACOES.REJECTED[1]];
  return SITUACOES[m.situacao] || [m.situacao, "bg-muted text-muted-foreground"];
}

/**
 * Avisos do pedido na loja que está no WhatsApp Oficial: fora das 24 horas a
 * Meta só entrega modelo aprovado. Mostra o texto padrão de cada aviso e se a
 * Meta já aprovou na conta da loja. Loja no QR Code: não mostra nada.
 */
export default function ModelosOficiais({ onVerPrevia, toast }) {
  const [dados, setDados] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [conferindo, setConferindo] = useState(false);

  useEffect(() => {
    cleoApi.modelosOficiais().then(setDados).catch(() => setDados({ oficial: false }));
  }, []);

  if (!dados?.oficial) return null;
  const modelos = dados.modelos || [];
  const aprovados = modelos.filter((m) => m.em_uso).length;
  const todos = aprovados === modelos.length;

  const conferir = async () => {
    setConferindo(true);
    try {
      const r = await cleoApi.conferirModelos();
      if (!r?.modelos) throw new Error(r?.detail || "Não foi possível conferir agora.");
      setDados(r);
    } catch (e) {
      toast?.({ title: "Erro ao conferir os modelos", description: e.message, variant: "destructive" });
    } finally {
      setConferindo(false);
    }
  };

  return (
    <div className="rounded-xl border border-green-500/40 bg-green-500/5 px-3 py-2.5 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" />
        <span className="text-sm font-semibold">WhatsApp Oficial: avisos por modelo aprovado</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${todos ? SITUACOES.APPROVED[1] : SITUACOES.PENDING[1]}`}>
          {aprovados} de {modelos.length} aprovados
        </span>
        <span className="flex-1" />
        <button type="button" onClick={conferir} disabled={conferindo}
          className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-2.5 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-60">
          {conferindo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Conferir agora
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Quem escreveu para a loja nas últimas 24 horas recebe os avisos abaixo, com o texto que você editou.
        Para os outros clientes o WhatsApp só entrega <b>modelo aprovado pela Meta</b>: nesse caso vai o texto padrão do
        Núcleo, com o nome da loja e o número do pedido. Entretenimento e outras mensagens sem modelo só saem dentro das 24 horas.
      </p>
      <button type="button" onClick={() => setAberto(!aberto)}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[#0a7d5a] dark:text-[#4ade80]">
        {aberto ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {aberto ? "Esconder os modelos" : "Ver os modelos e a situação de cada um"}
      </button>
      {aberto && (
        <ul className="space-y-1.5">
          {modelos.map((m) => {
            const [rotulo, cor] = selo(m);
            return (
              <li key={m.chave} className="rounded-lg border bg-background px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{m.rotulo}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cor}`}>{rotulo}</span>
                  <span className="flex-1" />
                  <button type="button" onClick={() => onVerPrevia({ titulo: `${m.rotulo} (modelo oficial)`, texto: m.texto, botao: m.botao })}
                    className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold hover:bg-muted">
                    <Smartphone className="h-3.5 w-3.5" /> Ver no celular
                  </button>
                </div>
                {!m.em_uso && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Enquanto não estiver aprovado, este aviso só sai para quem escreveu nas últimas 24 horas.
                    {m.motivo ? ` Motivo informado pela Meta: ${m.motivo}.` : ""}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
